import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Plus, Search, Trash2, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm";
const LABEL = "hk-tiny-label block mb-1.5";

/** "3 days ago" / "2 months ago" / "just now" formatting from an ISO date string. */
function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

export default function Customers() {
  const { user } = useAuth();
  const canWrite = ["sales", "sales_coordination", "super_admin", "dealer"].includes(user?.role);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [fName, setFName] = useState("");
  const [fAddress, setFAddress] = useState("");
  const [fAddedBy, setFAddedBy] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "", notes: "" });
  const [sortBy, setSortBy] = useState("created_at"); // name | location | added_by | created_at | projects_total
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    try { const { data } = await api.get("/customers"); setCustomers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setForm({ name: "", phone: "", email: "", location: "", notes: "" });
      setShowForm(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    let list = customers.filter((c) => {
      if (search) {
        const blob = `${c.name} ${c.phone || ""} ${c.email || ""} ${c.location || ""} ${c.created_by_name || ""}`.toLowerCase();
        if (!blob.includes(search.toLowerCase())) return false;
      }
      if (fName && !(`${c.name} ${c.phone || ""} ${c.email || ""}`).toLowerCase().includes(fName.toLowerCase())) return false;
      if (fAddress && !(c.location || "").toLowerCase().includes(fAddress.toLowerCase())) return false;
      if (fAddedBy && !(c.created_by_name || "").toLowerCase().includes(fAddedBy.toLowerCase())) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = (
        sortBy === "name" ? (a.name || "").toLowerCase() :
        sortBy === "location" ? (a.location || "").toLowerCase() :
        sortBy === "added_by" ? (a.created_by_name || "").toLowerCase() :
        sortBy === "projects_total" ? (a.projects_total || 0) :
        sortBy === "quotes_total" ? (a.quotes_total || 0) :
        (a.created_at || "")
      );
      const bv = (
        sortBy === "name" ? (b.name || "").toLowerCase() :
        sortBy === "location" ? (b.location || "").toLowerCase() :
        sortBy === "added_by" ? (b.created_by_name || "").toLowerCase() :
        sortBy === "projects_total" ? (b.projects_total || 0) :
        sortBy === "quotes_total" ? (b.quotes_total || 0) :
        (b.created_at || "")
      );
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return list;
  }, [customers, search, fName, fAddress, fAddedBy, sortBy, sortDir]);

  const flipSort = (k) => {
    if (sortBy === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(k); setSortDir(k === "projects_total" || k === "created_at" ? "desc" : "asc"); }
  };

  const roleLabel = (r) => {
    if (!r) return "";
    if (r === "super_admin") return "Super Admin";
    if (r === "sales_coordination") return "Sales Coord";
    return r.charAt(0).toUpperCase() + r.slice(1);
  };

  return (
    <DesktopLayout>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="hk-tiny-label">Directory</div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Customers</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {customers.length} customer{customers.length === 1 ? "" : "s"} · {customers.reduce((s, c) => s + (c.projects_total || 0), 0)} total projects
          </p>
        </div>
        {canWrite && (
          <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm" data-testid="customer-add-toggle">
            <Plus className="w-4 h-4" /> {showForm ? "Cancel" : "Add customer"}
          </button>
        )}
      </div>

      {canWrite && showForm && (
        <form onSubmit={submit} className="hk-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-form">
          <div>
            <label className={LABEL}>Name *</label>
            <input className={FIELD} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" />
          </div>
          <div>
            <label className={LABEL}>Phone</label>
            <input className={FIELD} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" data-testid="customer-phone" />
          </div>
          <div>
            <label className={LABEL}>Email</label>
            <input className={FIELD} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email" />
          </div>
          <div>
            <label className={LABEL}>Address</label>
            <input className={FIELD} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Street, city…" data-testid="customer-location" />
          </div>
          <div className="md:col-span-2">
            <label className={LABEL}>Notes</label>
            <textarea className={FIELD} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <button disabled={busy} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm disabled:opacity-60" data-testid="customer-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save customer
            </button>
          </div>
        </form>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all fields…"
            className="w-full bg-white border border-slate-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            data-testid="customer-search"
          />
        </div>
        <FilterInput label="Customer name" value={fName} onChange={setFName} testId="customer-filter-name" />
        <FilterInput label="Address" value={fAddress} onChange={setFAddress} testId="customer-filter-address" />
        <FilterInput label="Added by" value={fAddedBy} onChange={setFAddedBy} testId="customer-filter-addedby" />
        {(fName || fAddress || fAddedBy) && (
          <button type="button" onClick={() => { setFName(""); setFAddress(""); setFAddedBy(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="customer-filter-clear">Clear filters</button>
        )}
      </div>

      <div className="hk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customer-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[11px] font-bold border-b border-slate-200">
              <tr>
                <ThSort label="Customer name" active={sortBy === "name"} dir={sortDir} onClick={() => flipSort("name")} />
                <ThSort label="Address" active={sortBy === "location"} dir={sortDir} onClick={() => flipSort("location")} />
                <ThSort label="Added by" active={sortBy === "added_by"} dir={sortDir} onClick={() => flipSort("added_by")} />
                <ThSort label="Added since" active={sortBy === "created_at"} dir={sortDir} onClick={() => flipSort("created_at")} />
                <ThSort label="Projects" active={sortBy === "projects_total"} dir={sortDir} onClick={() => flipSort("projects_total")} align="right" />
                <ThSort label="Quotes" active={sortBy === "quotes_total"} dir={sortDir} onClick={() => flipSort("quotes_total")} align="right" />
                <th className="text-right px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-slate-100 hover:bg-orange-50/30 transition-colors"
                  data-testid={`customer-row-${c.id}`}
                >
                  <td className="px-4 py-3 align-top">
                    <Link to={`/customers/${c.id}`} className="block font-bold text-slate-900 hover:text-orange-600" data-testid={`customer-name-${c.id}`}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.phone && <span className="mr-2">{c.phone}</span>}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-slate-700">
                    {c.location || <span className="text-slate-400 italic">—</span>}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-semibold text-slate-800">{c.created_by_name || <span className="text-slate-400 italic">Unknown</span>}</div>
                    {c.created_by_role && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{roleLabel(c.created_by_role)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-slate-700 whitespace-nowrap" title={c.created_at || ""}>
                    {timeAgo(c.created_at)}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <div className="inline-flex items-center gap-2">
                      <span className="text-2xl font-black tabular-nums" style={{ fontFamily: "Chivo" }}>{c.projects_total || 0}</span>
                      <div className="flex flex-col gap-1 text-[10px] leading-none">
                        <span className="hk-badge hk-badge-progress py-0.5 px-1.5">{c.projects_active || 0} active</span>
                        <span className="hk-badge hk-badge-complete py-0.5 px-1.5">{c.projects_completed || 0} done</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <span className="text-2xl font-black tabular-nums text-sky-700" style={{ fontFamily: "Chivo" }} data-testid={`customer-quotes-${c.id}`}>
                      {c.quotes_total || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    {user?.role === "super_admin" && (
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const cascade = c.projects_total > 0
                            ? window.confirm(`Customer "${c.name}" has ${c.projects_total} project(s). Delete those too?\n\nOK = delete customer + all linked projects\nCancel = abort`)
                            : window.confirm(`Permanently delete customer "${c.name}"?`);
                          if (!cascade && c.projects_total > 0) return;
                          if (!cascade) return;
                          try {
                            await api.delete(`/customers/${c.id}${c.projects_total > 0 ? "?cascade=true" : ""}`);
                            toast.success(c.projects_total > 0 ? `Deleted + ${c.projects_total} project(s) removed` : "Customer deleted");
                            load();
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete customer"
                        data-testid={`customer-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-slate-500">
                    {customers.length === 0
                      ? "No customers yet — add one to start tracking repeat business."
                      : "No customers match this search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DesktopLayout>
  );
}

function ThSort({ label, active, dir, onClick, align = "left" }) {
  return (
    <th
      className={`px-4 py-3 cursor-pointer select-none hover:text-orange-600 ${align === "right" ? "text-right" : "text-left"}`}
      onClick={onClick}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? "text-orange-500" : "text-slate-300"}`} />
        {active && <span className="text-[9px] font-black text-orange-500">{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

function FilterInput({ label, value, onChange, testId }) {
  return (
    <div className="relative">
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        className="bg-white border border-slate-300 rounded-sm pl-3 pr-8 py-1.5 text-xs w-48 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
        data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800 text-xs">×</button>
      )}
    </div>
  );
}
